/**
 * ShiftPicker — komponent do wyboru aktywnego działu na dziś.
 *
 * Widoczny TYLKO gdy user ma > 1 dział w allowed_departments (multi-dept).
 * Owner/Manager/single-dept-employee → nie zobaczą tego komponentu.
 *
 * Wykorzystywany w:
 *  - Header (małe menu z badge)
 *  - Modal onboarding (przy pierwszym logowaniu multi-dept usera)
 */
import React, { useState } from "react";
import { useActiveShift } from "@/lib/shift-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, MapPin } from "lucide-react";

const DEPT_LABELS = {
  BAR: "Bar",
  SALA: "Sala",
  MASAZ: "Masaż",
};

const DEPT_COLORS = {
  BAR: "bg-amber-100 text-amber-900 border-amber-300",
  SALA: "bg-emerald-100 text-emerald-900 border-emerald-300",
  MASAZ: "bg-violet-100 text-violet-900 border-violet-300",
};

/**
 * Kompaktowy dropdown w headerze (widoczny na desktopie i mobile).
 */
export function ShiftPickerBadge() {
  const { activeShift, setActiveShift, isMultiDept, allowedDepartments } = useActiveShift();

  // Nie pokazuj dla single-dept i full-access userów
  if (!isMultiDept || !Array.isArray(allowedDepartments)) return null;

  const label = activeShift ? DEPT_LABELS[activeShift] || activeShift : "Wybierz dział";
  const colorClass = activeShift ? DEPT_COLORS[activeShift] : "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 border ${colorClass}`}
          data-testid="shift-picker-badge"
        >
          <MapPin className="h-4 w-4" />
          <span className="font-medium">Dziś: {label}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Zmień dział na dziś</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allowedDepartments.map((dept) => (
          <DropdownMenuItem
            key={dept}
            onSelect={() => setActiveShift(dept)}
            data-testid={`shift-picker-option-${dept.toLowerCase()}`}
            className={activeShift === dept ? "bg-slate-100 font-semibold" : ""}
          >
            <Badge variant="outline" className={`mr-2 ${DEPT_COLORS[dept] || ""}`}>
              {DEPT_LABELS[dept] || dept}
            </Badge>
            {activeShift === dept && <span className="ml-auto text-xs text-emerald-700">aktywny</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Modal onboarding — pokazywany przy pierwszym logowaniu multi-dept usera
 * (gdy activeShift jest null). User MUSI wybrać zanim będzie mógł kontynuować.
 */
export function ShiftPickerModal() {
  const { needsShiftPicker, setActiveShift, allowedDepartments } = useActiveShift();

  if (!needsShiftPicker || !Array.isArray(allowedDepartments)) return null;

  return (
    <Dialog open={needsShiftPicker} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="shift-picker-modal"
      >
        <DialogHeader>
          <DialogTitle>Wybierz dział na dziś</DialogTitle>
          <DialogDescription>
            Pracujesz w kilku działach — wskaż, w którym jesteś dzisiaj. Możesz to zmienić
            w każdej chwili z paska u góry.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          {allowedDepartments.map((dept) => (
            <Button
              key={dept}
              variant="outline"
              size="lg"
              className={`h-14 justify-start gap-3 text-base ${DEPT_COLORS[dept] || ""}`}
              onClick={() => setActiveShift(dept)}
              data-testid={`shift-picker-modal-choice-${dept.toLowerCase()}`}
            >
              <MapPin className="h-5 w-5" />
              <span className="font-semibold">{DEPT_LABELS[dept] || dept}</span>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
